// Simple client-side username/password auth backed by a JSON file.
//
// NOTE: This is obfuscation, not real security. The users file ships to the
// browser, so anyone can read it. Passwords are stored as SHA-256 hashes only
// to avoid keeping plaintext in the file. Do not use this to protect anything
// that actually matters.

const SESSION_KEY = "mockAppSession";

// SHA-256 hash a string -> lowercase hex, using the browser's Web Crypto API.
export async function sha256Hex(text) {
  const data = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// Load the users list from public/users.json.
async function loadUsers() {
  const res = await fetch(`${process.env.PUBLIC_URL || ""}/users.json`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error("Could not load users file.");
  const data = await res.json();
  return Array.isArray(data?.users) ? data.users : [];
}

// Validate credentials. Returns a session object on success, or null on failure.
export async function login(username, password) {
  const users = await loadUsers();
  const uname = String(username || "").trim();
  const match = users.find(
    (u) => String(u.username).toLowerCase() === uname.toLowerCase()
  );
  if (!match) return null;

  const hash = await sha256Hex(password);
  if (hash !== String(match.passwordHash).toLowerCase()) return null;

  return {
    username: match.username,
    displayName: match.displayName || match.username,
  };
}

// Session persistence (localStorage).
export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}
